import {filter, firstValueFrom, map, of, Subject, timeout} from 'rxjs';
import {v4 as uuid} from 'uuid';

import {
  ClientErrorResponse,
  ClientResponse,
  Conversation,
  ConversationContext,
  ConversationParticipant,
  initObservability,
  Observability,
  REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
  RequestAction,
} from '@stripe-internal/conversations-client';
import {createStore} from '@stripe-internal/state-manager';
import {
  InlineDataEventBus,
  InlineDataService,
} from '@stripe-internal/inline-data';
import {
  MessageFromParent,
  MessageFromParentType,
  MessageToParentType,
  RequestedAction,
  Visibility,
} from '@stripe-internal/support-conversations-controller';
import {TwilioClient} from '@stripe-internal/twilio-conversations-client';
import {UniversalHelpClient} from '@stripe-internal/universal-help-conversations-client';

import {Api} from './lib/api/api';
import {
  App,
  AppBusMessage,
  AppCloseHandler,
  AppConfig,
  AppProfile,
  AppState,
  AppStateStore,
} from './types';
import {ClientFetch} from './lib/util/clientFetch';
import {createApi} from './lib/api/createApi';
import {createClient} from './lib/createClient';
import {createParentMessageBus} from './lib/parentMessageBus/createParentMessageBus';
import {devLog} from './lib/util/dev';
import {getStoredConversation} from './lib/getStoredConversation';
import {isStartFreshTriggerPoint} from './lib/util/triggerPoints';
import {newInlineDataService} from './lib/inlineDataService';
import {ParentMessageBus} from './lib/parentMessageBus/ParentMessageBus';
import {requestVisibilityChange} from './lib/parentMessageBus/messages';
import {SupportConversationsClient} from './lib/SupportConversationsClient';
import {SupportTriggerPointTriggerPoint} from '../generated/types';
import {wrapFetch} from './lib/wrapFetch';

export class AppImpl implements App {
  private _stateStore: AppStateStore;

  private _api: Api;

  private _client: SupportConversationsClient;

  private _chatClient: TwilioClient;

  private _uhClient: UniversalHelpClient;

  private _inlineDataEventBus = new InlineDataEventBus();

  private _inlineDataService: InlineDataService;

  private _parentMessageBus: ParentMessageBus;

  private _parentMessageSubject = new Subject<AppBusMessage>();

  private _observability: Observability;

  private _handleRequestedAction:
    | ((action: RequestedAction) => void)
    | undefined;

  private _previousTriggerPoint?: string;

  private _conversationMap = new Map<string, Conversation>();

  constructor(
    config: AppConfig,
    profile: AppProfile,
    clientFetch: ClientFetch,
    handleRequestedAction?: (action: RequestedAction) => void,
    parentMessageBus?: ParentMessageBus,
    onClose?: AppCloseHandler,
    observability: Partial<Observability> = {},
  ) {
    const {triggerPoint} = config;
    this._stateStore = createStore<AppState>({
      initialized: false,
      currentConversationId: null,
      showCloseConfirmation: false,
      config,
      profile,
      triggerParams: triggerPoint ? {triggerPoint} : undefined,
      visibility: (config.initialVisibility as Visibility) ?? 'open',
      onClose,
    });

    const getConfig = () => this.config;
    const getProfile = () => this.profile;
    const wrappedFetch = wrapFetch(clientFetch, getConfig, getProfile);
    this._api = createApi(wrappedFetch, getProfile);

    this._handleRequestedAction = handleRequestedAction;

    this._inlineDataService = newInlineDataService(wrappedFetch, {
      audience: this.config.audience,
      entry_point: this.config.entryPoint,
    });

    this._parentMessageBus =
      parentMessageBus ??
      createParentMessageBus({
        audience: config.audience,
        entryPoint: config.entryPoint,
        parentOrigin: config.parentOrigin ?? window.origin,
      });

    this._observability = initObservability(observability);

    const getApi = () => this.api;
    const requestAction = (action: RequestAction) => this.requestAction(action);
    const getObservability = () => this._observability;

    const {client, chatClient, uhClient} = createClient({
      getConfig,
      getProfile,
      getApi,
      getObservability,
      inlineDataEventBus: this.inlineDataEventBus,
      requestAction,
    });
    this._client = client;
    this._chatClient = chatClient;
    this._uhClient = uhClient;

    this.stateStore.setValue((state) => {
      state.initialized = true;
    });

    this.handleParentMessage = this.handleParentMessage.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.minimize = this.minimize.bind(this);

    devLog('App initialized with config:', config);
    this.parentMessageBus.post({
      type: MessageToParentType.SetWidgetInitialized,
    });

    this.resumeExistingInProgressConversation();
  }

  get initialized() {
    return this.state.initialized;
  }

  get stateStore() {
    return this._stateStore;
  }

  get state() {
    return this.stateStore.getState();
  }

  get config() {
    return this.state.config;
  }

  get profile() {
    return this.state.profile;
  }

  hasFlag(name: string) {
    return !!this.state.profile.featureFlags[name];
  }

  get api() {
    return this._api;
  }

  get client() {
    return this._client;
  }

  // PLEASE DO NOT USE DIRECTLY. This is only exposed to bind to STP
  get chatClient() {
    return this._chatClient;
  }

  get inlineDataEventBus() {
    return this._inlineDataEventBus;
  }

  get inlineDataService() {
    return this._inlineDataService;
  }

  get parentMessageBus() {
    return this._parentMessageBus;
  }

  subscribeToParentMessageBus() {
    this.parentMessageBus.addListener(this.handleParentMessage);
  }

  unsubscribeFromParentMessageBus() {
    this.parentMessageBus.removeListener(this.handleParentMessage);
  }

  // maybe this should just be a part of parentMessageBus
  requestAction(action: RequestAction) {
    // the nonce will associate a response with our request
    const nonce = uuid();

    const {waitForResponse, responseTimeout, action: requestedAction} = action;

    if (this._handleRequestedAction) {
      this._handleRequestedAction(requestedAction);
      // faking a response value for now
      return Promise.resolve({code: 200});
    }

    const parentMessagesObs = this._parentMessageSubject.asObservable();

    let response: Promise<ClientResponse>;

    if (waitForResponse) {
      const timeoutResponse = {
        code: 400,
        error: `Timeout waiting for request-action response for ${action.action.name}`,
      };

      response = firstValueFrom(
        parentMessagesObs.pipe(
          // find the response message that matches our request nonce
          filter((m) => m.nonce === nonce),
          // map the response data a successful ClientResponse
          map((m) => {
            return {code: 200, data: m.data};
          }),
          // handle any timeouts
          timeout({
            // use a specified or default timeout
            each: responseTimeout ?? REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
            with: () => {
              // report timeouts
              // this.client.reportError(timeoutResponse.error);
              // return our defined timeout ClientResponse
              return of(timeoutResponse);
            },
          }),
        ),
      );
    } else {
      // if not waiting for a response, resolve immediately
      response = Promise.resolve({code: 200});
    }

    this.parentMessageBus.post({
      type: MessageToParentType.RequestAction,
      data: requestedAction,
      nonce,
    });

    return response;
  }

  get errors() {
    return this._observability.errors ?? {};
  }

  get metrics() {
    return this._observability.metrics ?? {};
  }

  get analytics() {
    return this._observability.analytics ?? {};
  }

  get visibility() {
    return this.state.visibility ?? 'open';
  }

  private getCurrentConversation(): Conversation | null {
    if (!this.state.currentConversationId) {
      return null;
    }

    return this.getConversation(this.state.currentConversationId) ?? null;
  }

  getConversation(conversationId: string): Conversation | undefined {
    return this._conversationMap.get(conversationId);
  }

  async startConversation({
    conversationId,
    initialQuery,
    restart = false,
    context,
  }: {
    conversationId?: string;
    initialQuery?: string;
    restart?: boolean;
    context?: Partial<ConversationContext>;
  } = {}): Promise<Conversation | null> {
    const {currentConversationId, triggerParams: triggerParamsFromState} =
      this.state;
    const {triggerPoint: triggerPointFromConfig} = this.config;

    const currentConversation = currentConversationId
      ? this.getConversation(currentConversationId)
      : undefined;

    if (
      restart &&
      currentConversation &&
      !currentConversation.isTabMostRecent()
    ) {
      return null;
    }

    try {
      const participant: ConversationParticipant = {
        id: this.profile.visitor.id,
        role: 'user',
        displayName: this.profile.visitor.displayName || 'User',
      };

      const newTriggerPoint =
        triggerParamsFromState?.triggerPoint ??
        triggerPointFromConfig ??
        'assistant_drawer_internal';
      let triggerParams = newTriggerPoint
        ? {
            triggerPoint: newTriggerPoint,
            initialQuery,
          }
        : undefined;

      if (restart) {
        triggerParams = {
          triggerPoint:
            (this._previousTriggerPoint as SupportTriggerPointTriggerPoint) ||
            newTriggerPoint,
          initialQuery,
        };
        currentConversation?.setContextValue('supressChatEndedNotice', true);
      }

      const contextParams: ConversationContext = {
        userId: participant.id,
        audience: this.config.audience,
        entryPoint: this.config.entryPoint,
        triggerParams,
        ...context,
      };

      if (restart && currentConversation) {
        contextParams.tabs = currentConversation.tabs;
      }

      let conversationIdToAttachTo = restart ? undefined : conversationId;

      if (restart || isStartFreshTriggerPoint(triggerParams?.triggerPoint)) {
        const placeholderResp = await this._client.startConversation({
          participant,
          context: {
            ...contextParams,
            placeholderConversation: true,
          },
        });

        if (!('error' in placeholderResp)) {
          const placeholderConversation = placeholderResp.data.conversation;
          this._conversationMap.set(
            placeholderConversation.id,
            placeholderConversation,
          );
          this.stateStore.setValue((state) => {
            state.currentConversationId = placeholderConversation.id;
          });
        }

        if (currentConversation) {
          await currentConversation.leave();
        } else {
          const prevConversationResp = await this._client.inProgress({
            context: {
              ...contextParams,
              channel: 'bot',
            },
          });
          if (!('error' in prevConversationResp)) {
            if (
              prevConversationResp.data.inProgress &&
              prevConversationResp.data.conversationId
            ) {
              const {conversationId: prevConversationId} =
                prevConversationResp.data;
              await this._client.leaveConversation({
                conversationId: prevConversationId,
                participant,
              });

              if (conversationIdToAttachTo === prevConversationId) {
                conversationIdToAttachTo = undefined;
              }
            }
          }
        }
      }

      const startContextParams = restart
        ? {}
        : {
            ...contextParams,
          };

      const resp = await this._client.startConversation({
        conversationId: conversationIdToAttachTo,
        participant,
        context: {
          userId: participant.id,
          audience: this.config.audience,
          entryPoint: this.config.entryPoint,
          triggerParams,
          ...startContextParams,
        },
      });

      if ('error' in resp) {
        if (conversationIdToAttachTo) {
          this._client.leaveConversation({
            conversationId: conversationIdToAttachTo,
            participant,
          });
        }

        const errorResponse = resp as ClientErrorResponse;
        this.stateStore.setValue((state) => {
          state.error = {
            message: errorResponse.error,
            errorId: errorResponse.sentryEventId,
          };
        });

        // TODO: need to make sure this prevents resumeOrStart continuing to
        //  try to start a conv or otherwise allow it to pass through without
        //  setting an error (and await the end)
        return null;
      }

      const newConversation = resp.data.conversation;
      this._conversationMap.set(newConversation.id, newConversation);
      this.stateStore.setValue((state) => {
        state.currentConversationId = newConversation.id;
      });

      this._previousTriggerPoint =
        newConversation.context.triggerParams?.triggerPoint;

      return newConversation;
    } catch (e) {
      this.stateStore.setValue((state) => {
        state.error = {
          message: (e as Error).message,
        };
      });
      return null;
    }
  }

  async restartConversation({
    initialQuery,
  }: {initialQuery?: string} = {}): Promise<Conversation | null> {
    return this.startConversation({
      initialQuery,
      restart: true,
    });
  }

  async endConversation(conversation?: Conversation): Promise<void> {
    if (conversation) {
      return this._endConversation(conversation);
    }

    return this._endCurrentConversation();
  }

  private async _endCurrentConversation() {
    const conversation = this.getCurrentConversation();
    if (conversation) {
      return this._endConversation(conversation);
    }
  }

  private async _endConversation(conversation: Conversation): Promise<void> {
    await conversation.leave();
    this._conversationMap.delete(conversation.id);

    if (this.state.currentConversationId === conversation.id) {
      this.stateStore.setValue((state) => {
        state.currentConversationId = null;
      });
    }
  }

  open() {
    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Open;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Open);
  }

  close(skipOnClose = false) {
    const conversation = this.getCurrentConversation();

    if (this.state.onClose && !skipOnClose) {
      const handled = this.state.onClose({
        close: (value) => this.close(value),
        conversation,
      });
      if (handled) {
        return;
      }
    }

    if (conversation) {
      const {channel, intent} = conversation.context;

      if (conversation.isActive) {
        if (intent === 'contact_support' && channel === 'request_a_call') {
          this.minimize();
          return;
        }

        if (!this.state.showCloseConfirmation && intent === 'contact_support') {
          this.stateStore.setValue((state) => {
            state.showCloseConfirmation = true;
          });
          return;
        }
      }

      if (channel !== 'bot' || this.config.closeEndsBotConversation) {
        this._endConversation(conversation);
      }
    }

    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Closed;
      state.currentConversationId = null;
      state.showCloseConfirmation = false;
      state.error = null;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Closed);
  }

  minimize() {
    this.stateStore.setValue((state) => {
      state.visibility = Visibility.Minimized;
    });

    requestVisibilityChange(this.parentMessageBus, Visibility.Minimized);
  }

  private async fetchExistingInProgressConversationData() {
    const {audience, entryPoint, triggerPoint} = this.config;
    const stored = getStoredConversation();

    const inProgressResp = await this.client.inProgressV2({
      // when we have an in-progress chat, we want to check if that specific chat
      // is in progress, otherwise we want to check if anything is in-progress,
      // which is why we otherwise leave it undefined
      conversationId:
        stored?.data?.context?.channel === 'chat' ? stored.id : undefined,
      context: stored?.data?.context ?? {audience, entryPoint, triggerPoint},
    });

    if ('error' in inProgressResp) {
      return;
    }

    if (inProgressResp.data.inProgress) {
      let conversationId: string | undefined = stored?.id;
      if (inProgressResp.data.conversationId) {
        conversationId = inProgressResp.data.conversationId;
      }

      let context: ConversationContext | undefined;
      if (stored && conversationId === stored.id) {
        context = {...stored.data?.context};
      }

      return {
        response: inProgressResp,
        conversationId,
        context,
      };
    } else if (stored) {
      Conversation.clearStorage({
        prefix: Conversation.storageKey(stored.id),
        all: true,
      });
    }

    return null;
  }

  async resumeOrStartConversation() {
    const existingInProgressData =
      await this.fetchExistingInProgressConversationData();
    if (existingInProgressData) {
      const {conversationId, context} = existingInProgressData;
      const conversation = await this.startConversation({
        conversationId,
        context,
      });

      if (conversation) {
        return conversation;
      }
    }

    return this.startConversation();
  }

  private async resumeExistingInProgressConversation() {
    const existingInProgressData =
      await this.fetchExistingInProgressConversationData();
    if (!existingInProgressData) {
      // Conversation.clearStorage({all: true});
      return;
    }

    const {response} = existingInProgressData;
    const {channel} = response.data;
    if (!channel || !['chat', 'request_a_call'].includes(channel)) {
      return;
    }

    this.open();
  }

  private handleParentMessage(messageFromParent: MessageFromParent) {
    if (
      messageFromParent.type === MessageFromParentType.VisibilityChange &&
      ['open', 'closed', 'minimized'].includes(
        messageFromParent.data?.visibility,
      )
    ) {
      this.stateStore.setValue((state) => {
        state.visibility = messageFromParent.data.visibility;
        // open callers will always supply trigger params, but
        // if we RequestVisibilityChange to open, the controller will
        // respond just with the visibility change and no trigger params
        // so we don't want to clear out the trigger params in that case
        if (messageFromParent.data.triggerParams) {
          state.triggerParams = messageFromParent.data.triggerParams;
        }
      });
    }

    if (messageFromParent.type === MessageFromParentType.CheckInitialized) {
      this.parentMessageBus.post({
        type: MessageToParentType.SetWidgetInitialized,
      });
    }

    this._parentMessageSubject.next(messageFromParent);
  }
}
